import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Drone groups — named sets of robots that section the fleet sidebar.
 * Groups created without a name take the next free greek letter (Alpha,
 * Beta, Gamma, …); robots in no group render under the next letter after
 * the last named group.
 */
export interface DroneGroup {
  id: string;
  name: string;
  robot_ids: string[];
  created_at: number;
}

interface DroneGroupsStore {
  groups: Record<string, DroneGroup>;
  create: (g: { name?: string; robot_ids: string[] }) => DroneGroup;
  remove: (id: string) => void;
  rename: (id: string, name: string) => void;
  setMembers: (id: string, robotIds: string[]) => void;
}

export const GREEK_SEQUENCE = [
  'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta',
  'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Omicron', 'Pi',
] as const;

/** First greek letter not already used as a group name (case-insensitive). */
export function nextGreekName(groups: Record<string, DroneGroup>): string {
  const used = new Set(Object.values(groups).map((g) => g.name.toLowerCase()));
  for (const n of GREEK_SEQUENCE) {
    if (!used.has(n.toLowerCase())) return n;
  }
  return `Group ${Object.keys(groups).length + 1}`;
}

function makeId(): string {
  return `g-${Math.random().toString(36).slice(2, 10)}`;
}

export const useDroneGroups = create<DroneGroupsStore>()(
  persist(
    (set, get) => ({
      groups: {},
      create: ({ name, robot_ids }) => {
        const fresh: DroneGroup = {
          id: makeId(),
          name: name?.trim() || nextGreekName(get().groups),
          robot_ids,
          created_at: Date.now(),
        };
        set((s) => ({ groups: { ...s.groups, [fresh.id]: fresh } }));
        return fresh;
      },
      remove: (id) =>
        set((s) => {
          const next = { ...s.groups };
          delete next[id];
          return { groups: next };
        }),
      rename: (id, name) =>
        set((s) => {
          const cur = s.groups[id];
          if (!cur || !name.trim()) return s;
          return { groups: { ...s.groups, [id]: { ...cur, name: name.trim() } } };
        }),
      setMembers: (id, robot_ids) =>
        set((s) => {
          const cur = s.groups[id];
          if (!cur) return s;
          return { groups: { ...s.groups, [id]: { ...cur, robot_ids } } };
        }),
    }),
    // v2: v1 shipped seeded demo groups with colours/pins; key bump drops them.
    { name: 'fabricos.droneGroups.v2' },
  ),
);
